import React from "react";
import {
    Card,
    Form,
    Input,
    Button,
    Upload,
    Avatar,
    Space,
    Typography,
    Alert,
    Divider,
    message,
    Tabs,
    Tag,
} from "antd";
import {
    FiGrid,
    FiTarget,
    FiBell,
    FiUser,
    FiCreditCard,
    FiLayers,
    FiShield,
    FiMail,
    FiPhone,
    FiImage,
    FiCamera,
    FiSave,
    FiMapPin,
    FiHome,
    FiStar,
} from "react-icons/fi";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Layout from "../../components/layout/Layout";
import MemberServiceInstance from "../../services/MemberService";
import { setFileUrl, setFormFieldValue } from "../../helpers/FormHelper";
import { getErrorMessage } from "../../helpers/ApiResponseHelper";
import { getCapitalizedString, getLowerCaseString } from "../../helpers/StringFormatHelper";
import { setAddressData, setMemberData } from "../../store/slices/MemberSlice";

const { Title, Text } = Typography;

function Subnav() {
    const LINKS = [
        { to: "/dashboard", label: "Dashboard", Icon: FiGrid },
        { to: "/my/predictions", label: "My predictions", Icon: FiTarget },
        { to: "/notifications", label: "Notifications", Icon: FiBell },
        { to: "/account/profile", label: "My profile", Icon: FiUser },
        { to: "/account/billing", label: "Billing", Icon: FiCreditCard },
        { to: "/plans", label: "Plans & Pricing", Icon: FiLayers },
        { to: "/account/security", label: "Change password", Icon: FiShield },
    ];
    const { pathname } = useLocation();
    const isActive = (to) => pathname === to || (to !== "/" && pathname.startsWith(to));

    return (
        <nav className="px-subnav" aria-label="Account navigation">
            <div className="px-subnav__inner">
                {LINKS.map(({ to, label, Icon }) => (
                    <Link key={to} to={to} className={`px-subnav__link ${isActive(to) ? "is-active" : ""}`}>
                        <Icon aria-hidden />
                        <span>{label}</span>
                    </Link>
                ))}
            </div>
        </nav>
    );
}

export default function Profile() {
    const member = useSelector((state) => state.Member.data);
    const address = useSelector((state) => state.Member.address);
    const {
        name,
        surname,
        fullName,
        email,
        phone,
        freeTokens,
        photo,
        subscription = { name: "Free", badge: "Free tier" },
    } = member;
    const dispatch = useDispatch();
    const [profileForm] = Form.useForm();
    const [addressForm] = Form.useForm();
    const token = useSelector((state) => state.Auth.token);
    const [savingProfile, setSavingProfile] = React.useState(false);
    const [savingAddress, setSavingAddress] = React.useState(false);
    const [avatar, setAvatar] = React.useState(setFileUrl(photo) || "");
    const [uploading, setUploading] = React.useState(false);
    const [errorMsg, setErrorMsg] = React.useState(null);
    const [successMsg, setSuccessMsg] = React.useState(null);

    React.useEffect(() => {
        profileForm.setFieldsValue({ name, surname, email, phone });
        addressForm.setFieldsValue({
            country: address?.country || "",
            city: address?.city || "",
            street: address?.street || "",
            appartment: address?.appartment || "",
            postalCode: address?.postalCode || "",
        });
    }, [fullName, email, phone, address, profileForm, addressForm]);

    const beforeUpload = (file) => {
        const isImage = /image\/(png|jpeg|jpg|webp)/.test(file.type);
        if (!isImage) {
            message.error("Only PNG/JPEG/WEBP files are allowed.");
            return Upload.LIST_IGNORE;
        }
        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
            message.error("Image must be smaller than 5MB.");
            return Upload.LIST_IGNORE;
        }
        return true;
    };

    const handleAvatarChange = async ({ file }) => {
        if (!file || file.status === "removed") return;
        setUploading(true);
        try {
            const requestData = new FormData();
            requestData.append('name', getCapitalizedString(name));
            requestData.append('surname', getCapitalizedString(surname));
            requestData.append('photo', file.originFileObj);
            const memberUuid = setFormFieldValue(member?.uuid);
            const response = await MemberServiceInstance.updatePhoto(token, requestData, memberUuid);
            const responseData = response.data;
            const { success, result, message } = responseData;
            if (success === true) {
                dispatch(setMemberData(result));
                setAvatar(setFileUrl(result?.photo));
                await message.success("Avatar uploaded.");
            } else {
                await message.error(message);
            }
        } catch (e) {
            await message.error("Failed to upload avatar.");
        } finally {
            setUploading(false);
        }
    };

    const formatProfileRequestData = (data) => {
        return {
            name: getCapitalizedString(data?.name),
            surname: getCapitalizedString(data?.surname),
            fullName: `${getCapitalizedString(data?.name)} ${getCapitalizedString(data?.surname)}`,
            email: getLowerCaseString(data?.email),
            phone: setFormFieldValue(data?.phone),
        };
    };

    const onSaveProfile = async () => {
        setErrorMsg(null); setSuccessMsg(null);
        try {
            setSavingProfile(true);
            const data = await profileForm.validateFields();
            const requestData = formatProfileRequestData(data);
            const memberUuid = setFormFieldValue(member?.uuid);
            const response = await MemberServiceInstance.update(token, requestData, memberUuid);
            const responseData = response.data;
            const { success, result, message } = responseData;
            if (success === true) {
                dispatch(setMemberData(result));
                setSuccessMsg("Profile details saved.");
            } else {
                setErrorMsg(message);
            }
        } catch (error) {
            if (error?.values) {
                setSavingProfile(false);
                return;
            }
            const errorMessage = getErrorMessage(error?.response, error?.message);
            setErrorMsg(errorMessage);
        } finally {
            setSavingProfile(false);
        }
    };

    const formatAddressRequestData = (data) => {
        return {
            country: getCapitalizedString(data?.country),
            city: getCapitalizedString(data?.city),
            street: getCapitalizedString(data?.street),
            appartment: getCapitalizedString(data?.appartment),
            postalCode: getCapitalizedString(data?.postalCode),
        };
    };

    const onSaveAddress = async () => {
        setErrorMsg(null); setSuccessMsg(null);
        try {
            setSavingAddress(true);
            const data = await addressForm.validateFields();
            const requestData = formatAddressRequestData(data);
            const memberUuid = setFormFieldValue(address?.uuid);
            const response = await MemberServiceInstance.completeProfile(token, requestData, memberUuid);
            const responseData = response.data;
            const { success, result, message } = responseData;
            if (success === true) {
                dispatch(setAddressData(result));
                setSuccessMsg("Address details saved.");
            } else {
                setErrorMsg(message);
            }
        } catch (error) {
            if (error?.values) {
                setSavingAddress(false);
                return;
            }
            const errorMessage = getErrorMessage(error?.response, error?.message);
            setErrorMsg(errorMessage);
        } finally {
            setSavingAddress(false);
        }
    };

    const ui = {
        heroWrap: {
            position: "relative",
            padding: "80px 16px 80px",
            background:
                "radial-gradient(1200px 280px at 50% -30%, rgba(59,130,246,0.25), transparent 60%), linear-gradient(180deg, #0b1020 0%, #0b1020 40%, #0c1428 100%)",
            color: "white",
            borderBottom: "1px solid rgba(255,255,255,.06)",
        },
        heroInner: { maxWidth: 1350, margin: "0 auto" },
        badge: {
            display: "inline-flex", alignItems: "center", gap: 8,
            fontSize: 12, color: "#93c5fd", background: "rgba(147,197,253,.10)",
            border: "1px solid rgba(147,197,253,.30)", padding: "6px 12px", borderRadius: 999,
        },
        heroTitle: { margin: "14px 0 10px", fontSize: 36, lineHeight: 1.15, fontWeight: 900, color: "#fff", letterSpacing: "-0.02em" },
        heroSub: { margin: 0, color: "rgba(255,255,255,.85)", maxWidth: 820 },
    };

    return (
        <Layout headerStyle={1} footerStyle={1} breadcrumbTitle="My Profile">
            <section style={ui.heroWrap} aria-label="Hero">
                <div style={ui.heroInner}>
                    <span style={ui.badge}><FiUser /> My Profile</span>
                    <h1 style={ui.heroTitle}>Manage your profile & address</h1>
                    <p style={ui.heroSub}>Keep your personal information up to date and personalize your avatar.</p>
                </div>
            </section>

            <Subnav />

            <section className="px-profile">
                <div className="px-wrap">
                    <aside className="px-side">
                        <Card className="px-identity" bordered={false}>
                            <div className="px-identity__header" style={{ textAlign: 'center' }}>
                                <div className="row">
                                    <div className="col-12">
                                        <Avatar
                                            src={avatar}
                                            size={96}
                                            style={{ backgroundColor: "#1e40af" }}
                                            icon={<FiImage />}
                                        >
                                            {!avatar && initials(fullName || email)}
                                        </Avatar>
                                    </div>
                                    <div className="col-12" style={{ textAlign: 'center' }}>
                                        <Upload
                                            accept="image/png,image/jpeg,image/webp"
                                            showUploadList={false}
                                            beforeUpload={beforeUpload}
                                            customRequest={({ file, onSuccess }) => {
                                                handleAvatarChange({ file: { originFileObj: file } });
                                                onSuccess && onSuccess("ok");
                                            }}
                                        >
                                            <Button size="small" icon={<FiCamera />} loading={uploading} className="px-avatar-btn">
                                                Change
                                            </Button>
                                        </Upload>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12" style={{ textAlign: 'center', marginTop: 12 }}>
                                    <Title level={3}>{fullName || "Your Name"}</Title>
                                    <Text>{email || "your@email.com"}</Text>
                                    <div style={{ marginTop: 8 }}>
                                        <Tag color="blue">{`${subscription?.name} Tier` || "Free tier"}</Tag>
                                        <Link to="/plans" className="px-identity__manage">Manage plan</Link>
                                    </div>
                                </div>
                            </div>

                            <Divider style={{ margin: "12px 0" }} />

                            <div className="px-quick">
                                <div className="px-quick__item">
                                    <span className="px-quick__label"><FiPhone /> Phone</span>
                                    <span className="px-quick__value">{phone || "—"}</span>
                                </div>
                                <div className="px-quick__item">
                                    <span className="px-quick__label"><FiStar /> Free tokens</span>
                                    <span className="px-quick__value">{freeTokens || "—"}</span>
                                </div>
                            </div>
                        </Card>
                    </aside>

                    <main className="px-main">
                        <Card className="px-card register-modal" styles={{ body: { paddingTop: 10 } }}>
                            <Tabs
                                defaultActiveKey="profile"
                                className="px-tabs"
                                items={[
                                    {
                                        key: "profile",
                                        label: (
                                            <span className="px-tab">
                                                <FiUser /> <span>Profile</span>
                                            </span>
                                        ),
                                        children: (
                                            <Form form={profileForm} className="register-modal" layout="vertical">
                                                <div className="px-grid" style={{ marginTop: 20 }}>
                                                    <Form.Item
                                                        name="name"
                                                        label={<Label icon={<FiUser />} text="Name" />}
                                                        rules={[{ required: true, message: "Please enter your name" }]}
                                                        extra="This will be used across your invoices and predictions."
                                                    >
                                                        <Input placeholder="Your name" />
                                                    </Form.Item>

                                                    <Form.Item
                                                        name="email"
                                                        label={<Label icon={<FiMail />} text="Email" />}
                                                        rules={[
                                                            { required: true, message: "Please enter your email" },
                                                            { type: "email", message: "Enter a valid email" },
                                                        ]}
                                                        extra="We’ll use this for receipts and critical account alerts."
                                                    >
                                                        <Input placeholder="you@company.com" />
                                                    </Form.Item>

                                                    <Form.Item
                                                        name="surname"
                                                        label={<Label icon={<FiUser />} text="Surame" />}
                                                        rules={[{ required: true, message: "Please enter your surname" }]}
                                                        extra="This will be used across your invoices and predictions."
                                                    >
                                                        <Input placeholder="Your surname" />
                                                    </Form.Item>

                                                    <Form.Item
                                                        name="phone"
                                                        label={<Label icon={<FiPhone />} text="Phone" />}
                                                        rules={[{ required: true, pattern: /^[\d+()[\]\s-]{6,}$/, message: "Enter a valid phone" }]}
                                                        extra="Optional — helps with urgent support."
                                                    >
                                                        <Input placeholder="+49 123 456 789" />
                                                    </Form.Item>
                                                </div>

                                                <div className="row">
                                                    <div className="col-12">
                                                        {errorMsg && <Alert type="error" style={{ marginTop: 10 }} showIcon message={errorMsg} closable onClose={() => setErrorMsg(null)} />}
                                                        {successMsg && <Alert type="success" style={{ marginTop: 10 }} showIcon message={successMsg} closable onClose={() => setSuccessMsg(null)} />}
                                                    </div>
                                                </div>

                                                <div className="px-sticky-actions">
                                                    <Button
                                                        type="primary"
                                                        size="large"
                                                        icon={<FiSave />}
                                                        loading={savingProfile}
                                                        onClick={onSaveProfile}
                                                    >
                                                        Save profile
                                                    </Button>
                                                </div>
                                            </Form>
                                        ),
                                    },
                                    {
                                        key: "address",
                                        label: (
                                            <span className="px-tab">
                                                <FiHome /> <span>Address</span>
                                            </span>
                                        ),
                                        children: (
                                            <Form form={addressForm} layout="vertical">
                                                <div className="px-grid" style={{ marginTop: 20 }}>
                                                    <Form.Item
                                                        name="street"
                                                        label={<Label icon={<FiHome />} text="Street" />}
                                                        rules={[{ required: true, message: "Please enter street" }]}
                                                    >
                                                        <Input size="large" placeholder="Street" />
                                                    </Form.Item>

                                                    <Form.Item
                                                        name="country"
                                                        label={<Label icon={<FiMapPin />} text="Country" />}
                                                        rules={[{ required: true, message: "Please enter your country" }]}
                                                    >
                                                        <Input size="large" placeholder="Country" />
                                                    </Form.Item>

                                                    <Form.Item
                                                        name="appartment"
                                                        label={<Label icon={<FiHome />} text="Appartment" />}
                                                        rules={[{ required: true, message: "Please enter appartment" }]}
                                                    >
                                                        <Input size="large" placeholder="Apartment, suite, etc." />
                                                    </Form.Item>

                                                    <Form.Item
                                                        name="city"
                                                        label={<Label icon={<FiMapPin />} text="City" />}
                                                        rules={[{ required: true, message: "Please enter your city" }]}
                                                    >
                                                        <Input size="large" placeholder="City" />
                                                    </Form.Item>

                                                    <Form.Item
                                                        name="postalCode"
                                                        label={<Label icon={<FiMapPin />} text="Postal code" />}
                                                        rules={[{ required: true, message: "Please enter your postal code" }]}
                                                    >
                                                        <Input size="large" placeholder="Postal code" />
                                                    </Form.Item>

                                                    <Form.Item
                                                        name="county"
                                                        label={<Label icon={<FiMapPin />} text="County" />}
                                                    >
                                                        <Input size="large" placeholder="County" />
                                                    </Form.Item>
                                                </div>

                                                <div className="row">
                                                    <div className="col-12">
                                                        {errorMsg && <Alert type="error" style={{ marginTop: 10 }} showIcon message={errorMsg} closable onClose={() => setErrorMsg(null)} />}
                                                        {successMsg && <Alert type="success" style={{ marginTop: 10 }} showIcon message={successMsg} closable onClose={() => setSuccessMsg(null)} />}
                                                    </div>
                                                </div>

                                                <div className="px-sticky-actions">
                                                    <Button
                                                        type="primary"
                                                        size="large"
                                                        icon={<FiSave />}
                                                        loading={savingAddress}
                                                        onClick={onSaveAddress}
                                                    >
                                                        Save address
                                                    </Button>
                                                </div>
                                            </Form>
                                        ),
                                    },
                                ]}
                            />
                        </Card>
                    </main>
                </div>

                <style>{`
          .px-profile { background:#f7f7f9; padding:30px; }
          .px-wrap {
            max-width: 1400px; margin: 0 auto;
            display: grid; gap: 22px;
            grid-template-columns: 380px 1fr;
          }
          @media (max-width: 1100px) { .px-wrap { grid-template-columns: 1fr; } }
          @media (max-width: 520px) { .px-profile { padding:16px; } }

          /* Identity card */
          .px-identity {
            border-radius: 18px;
            padding: 18px;
            background: linear-gradient(180deg, #fff, #fafafa);
            border: 1px solid rgba(0,0,0,.06);
            box-shadow: 0 8px 24px rgba(0,0,0,.06);
          }
          .px-identity__header {
            align-items: center;
          }
          @media (max-width: 480px) {
            .px-identity__header { text-align: center; }
          }
          .px-avatar-wrap { position:relative; display:inline-block; }
          .px-avatar-btn {
            margin-top:8px;
            border-radius:999px; padding:0 10px; height:28px;
          }
          .px-identity__name { margin: 0 0 4px !important; }
          .px-identity__email { color:#64748b; display:block; }
          .px-identity__plan { margin-top: 8px; display:flex; gap:10px; align-items:center; flex-wrap:wrap; }
          .px-identity__manage { font-size:12px; font-weight:700; color:#1d4ed8; text-decoration:none; }

          .px-quick {
            margin-top: 8px;
            display:grid; gap:8px;
            grid-template-columns: repeat(2, minmax(0,1fr));
          }
          @media (max-width: 520px) { .px-quick { grid-template-columns: 1fr; } }
          .px-quick__item {
            background:#fff; border:1px solid rgba(0,0,0,.06);
            border-radius: 12px; padding: 10px;
            display:flex; flex-direction:column; gap:4px;
          }
          .px-quick__label { font-size:12px; color:#64748b; display:flex; gap:6px; align-items:center; }
          .px-quick__value { font-weight:700; color:#0f172a; }

          /* Main card / tabs */
          .px-card {
            border-radius:18px;
            background:#fff;
            border:1px solid rgba(0,0,0,.06);
            box-shadow: 0 10px 30px rgba(0,0,0,.06);
          }
          .px-tabs .ant-tabs-nav { margin: 0 0 4px 0; }
          .px-tab { display:inline-flex; gap:8px; align-items:center; font-weight:700; }

          .px-grid {
            display:grid; gap:12px;
            grid-template-columns: repeat(2, minmax(0,1fr));
          }
          @media (max-width: 768px) { .px-grid { grid-template-columns: 1fr; } }

          /* Sticky save area with subtle glass effect */
          .px-sticky-actions {
            position: sticky; bottom: -1px; z-index: 5;
            padding: 12px 0 0;
            display: flex; justify-content: flex-end;
            background: linear-gradient(180deg, transparent, #fff 30%);
          }

          /* Account subnav (shared) */
          .px-subnav {
            position: sticky; top: 0; z-index: 20;
            backdrop-filter: blur(6px);
            background: rgba(255,255,255,.85);
            border-bottom: 1px solid rgba(2,6,23,.08);
          }
          .px-subnav__inner {
            max-width: 1400px; margin: 0 auto;
            padding: 10px 16px;
            display: flex; gap: 8px; align-items: center; flex-wrap: nowrap;
            overflow-x: auto; -webkit-overflow-scrolling: touch;
          }
          .px-subnav__link {
            display: inline-flex; align-items: center;
            padding: 8px 12px; gap: 8px;
            border-radius: 999px;
            font-size: 13px; font-weight: 700;
            color: #334155;
            border: 1px solid transparent;
            white-space: nowrap; text-decoration:none;
          }
          .px-subnav__link:hover { background: #f1f5f9; border-color: rgba(2,6,23,.08); }
          .px-subnav__link.is-active {
            color: #1e3a8a;
            background: rgba(37,99,235,.10);
            border-color: rgba(37,99,235,.25);
          }
        `}</style>
            </section>
        </Layout>
    );
}

function Label({ icon, text }) {
    return (
        <Space align="center" size={8}>
            <span aria-hidden>{icon}</span>
            <span style={{ fontWeight: 600 }}>{text}</span>
        </Space>
    );
}

function initials(str = "") {
    const parts = String(str).trim().split(/\s+/).slice(0, 2);
    const val = parts.map((p) => p[0]?.toUpperCase()).join("");
    return val || "U";
}
